import { addWeeks, format, parseISO } from 'date-fns';

// Pure date logic for the class scheduler. Kept free of Supabase/React so
// it can be unit-tested (see schedule.test.ts).
//
// Two jobs:
//  1. When scheduling a class: generate its weekly session start times,
//     skipping holidays (a skipped week pushes the series later, so an
//     8-week course is always 8 sessions).
//  2. When a new no-class day is added: figure out which existing
//     sessions must move, sliding each affected series one week later.

export function dateKey(d: Date): string {
  return format(d, 'yyyy-MM-dd');
}

export interface GeneratedStarts {
  starts: Date[];
  // Holiday dates the series skipped over (for showing "skips Aug 4").
  skipped: Date[];
}

export function generateSessionStarts(
  first: Date,
  count: number,
  holidays: ReadonlySet<string>,
): GeneratedStarts {
  const starts: Date[] = [];
  const skipped: Date[] = [];
  let cursor = first;
  // Guard: even a calendar packed with holidays terminates.
  let guard = count + 104;
  while (starts.length < count && guard-- > 0) {
    if (holidays.has(dateKey(cursor))) skipped.push(cursor);
    else starts.push(cursor);
    // addWeeks keeps the local wall-clock time across DST changes —
    // "Tuesdays at 6pm" stays 6pm in March and November.
    cursor = addWeeks(cursor, 1);
  }
  return { starts, skipped };
}

// For a recurring class whose current block has run out, generates the
// next block of `count` weekly sessions, starting one week after the
// last session that's already on the calendar.
export function generateNextBlock(
  lastSessionStart: Date,
  count: number,
  holidays: ReadonlySet<string>,
): GeneratedStarts {
  return generateSessionStarts(addWeeks(lastSessionStart, 1), count, holidays);
}

export interface ShiftableSession {
  id: string;
  class_id: string;
  starts_at: string; // ISO
  ends_at: string; // ISO
}

export interface SessionShift {
  id: string;
  starts_at: string;
  ends_at: string;
}

// A date became a no-class day. For every class with a scheduled session
// on that date, that session and every later session in the same class
// slide one week later (order and weekly rhythm preserved). If a slid
// session lands on another holiday, it slides again — and the extra
// offset carries to the sessions after it so the series never collides
// with itself.
export function planHolidayShift(
  sessions: readonly ShiftableSession[],
  holidayDate: string, // yyyy-MM-dd
  otherHolidays: ReadonlySet<string>,
): SessionShift[] {
  const holidays = new Set(otherHolidays);
  holidays.add(holidayDate);

  const byClass = new Map<string, ShiftableSession[]>();
  for (const s of sessions) {
    const arr = byClass.get(s.class_id);
    if (arr) arr.push(s);
    else byClass.set(s.class_id, [s]);
  }

  const shifts: SessionShift[] = [];
  for (const classSessions of byClass.values()) {
    const ordered = [...classSessions].sort((a, b) => a.starts_at.localeCompare(b.starts_at));
    const hitIndex = ordered.findIndex((s) => dateKey(parseISO(s.starts_at)) === holidayDate);
    if (hitIndex === -1) continue; // this class isn't affected

    let offsetWeeks = 0;
    for (let i = hitIndex; i < ordered.length; i++) {
      const s = ordered[i];
      const start = parseISO(s.starts_at);
      const end = parseISO(s.ends_at);
      if (i === hitIndex) offsetWeeks = 1;
      let candidate = addWeeks(start, offsetWeeks);
      let guard = 104;
      while (holidays.has(dateKey(candidate)) && guard-- > 0) {
        offsetWeeks++;
        candidate = addWeeks(start, offsetWeeks);
      }
      shifts.push({
        id: s.id,
        starts_at: candidate.toISOString(),
        ends_at: addWeeks(end, offsetWeeks).toISOString(),
      });
    }
  }
  return shifts;
}
